// Session-folder mint helper — single source of truth for the
// `YYMMDD_<sanitized-name>_<shortId>` format used to name a talent's
// upload bucket. Mirrors vanilla `confirmName` (legacy/index.html
// lines 3952-3964): the folder is fixed once, on Welcome submit, and
// reused for every per-asset upload in the session.
//
// Two callers:
//   1. `src/pages/Welcome.tsx` — happy path on Start.
//   2. `src/pages/ToolLayout.tsx` — lazy mint after a hydrated session
//      restored from IndexedDB has a name but no folder. This protects
//      pre-fix sessions (commit 314cd87 introduced the Welcome mint;
//      sessions saved before that have `sessionFolder: ''`, which keeps
//      the Upload Version button gated forever — see `uploadDisabled`
//      in TTSView/STSView). Without lazy mint the user has to "Start
//      Fresh" to recover, losing all in-flight takes.
export function mintSessionFolder(name: string): {
  folder: string;
  shortId: string;
} {
  // Transliterate German umlauts BEFORE the alphanumeric strip so "Müller"
  // becomes "Mueller" instead of "M__ller". Mirrors sanitizeForFilename
  // (src/lib/uploadFilenames.ts) so the folder name stays in sync with how
  // the asset filename builders treat the same input. After that, collapse
  // any remaining non-ASCII char or punctuation into a single underscore
  // and trim leading/trailing underscores so the folder isn't padded with
  // separators ("Maria Müller " → "Maria_Mueller", not "_Maria_Mueller_").
  // Falls back to "user" when the entire input transliterates away (e.g.
  // CJK-only names) so the folder shape stays parseable.
  const trimmed = name.trim();
  const transliterated = trimmed
    .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue')
    .replace(/Ä/g, 'Ae').replace(/Ö/g, 'Oe').replace(/Ü/g, 'Ue')
    .replace(/ß/g, 'ss');
  const slug =
    transliterated.normalize('NFD').replace(/\p{M}+/gu, '').replace(/[^a-zA-Z0-9]+/g, '_').replace(/^_+|_+$/g, '') ||
    'user';
  const now = new Date();
  const yy = String(now.getFullYear()).slice(2);
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  const shortId = randomShortId();
  return { folder: `${yy}${mm}${dd}_${slug}_${shortId}`, shortId };
}

const SHORT_ID_ALPHABET = 'abcdefghijklmnopqrstuvwxyz0123456789';
const SHORT_ID_LENGTH = 8;

/** 8 Zeichen [a-z0-9] aus `crypto.getRandomValues` (Audit M1, 2026-09-24).
 *  Der Session-Ordner ist zusammen mit Projekt-ID und Passwort der Schlüssel
 *  zum Arbeitsstand — vorher 4 Zeichen aus `Math.random` (≈ 1,7 Mio.
 *  Möglichkeiten, dazu Datum + Name erratbar). Jetzt 36^8 ≈ 2,8 · 10^12.
 *  Rejection Sampling: nur Bytes < 252 (= 7 · 36), sonst wären die ersten
 *  Zeichen des Alphabets leicht bevorzugt. Ältere Sessions mit 4 Zeichen
 *  bleiben gültig — der Dienst prüft nur das Alphabet (server/paths.ts). */
function randomShortId(): string {
  const out: string[] = [];
  const buf = new Uint8Array(16);
  while (out.length < SHORT_ID_LENGTH) {
    crypto.getRandomValues(buf);
    for (const b of buf) {
      if (b >= 252) continue;
      out.push(SHORT_ID_ALPHABET[b % 36]);
      if (out.length === SHORT_ID_LENGTH) break;
    }
  }
  return out.join('');
}
